import React from "react";
import { cn } from "@/lib/utils";
import {
  CAPTION_PREVIEW_TEXT,
  CAPTION_PREVIEW_CHARACTER_START_TIMES,
} from "@repo/video-engine-i2v/components/Captions/CaptionPresets";
import { IconX } from "@tabler/icons-react";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Button } from "@/lib/components/Button/Button";
import { Player, PlayerRef } from "@remotion/player";
import CaptionsFilters, {
  Filters,
  getFilteredCaptions,
} from "./CaptionsFilters";
import { Captions as CaptionsComponent } from "@repo/video-engine-i2v/components/Captions/Captions";
import { FontContext } from "@repo/video-engine-i2v/utils/font";
import CaptionSettings from "./CaptionSettings";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useCustomCaptionsQuery } from "@/lib/queries/caption-queries";
import { useFontsByExternalIdsQuery } from "@/lib/queries/font-queries";
import { CaptionConfigDto, CaptionDto } from "@repo/api/caption";
import { FontDto } from "@repo/api/font";
import { Checkbox } from "@/lib/components/Checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import Toast from "@/lib/components/Toast";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { CompositionAspectRatioDto } from "@repo/api/aspect-ratio";

export default function Captions() {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const [selectedFilter, setSelectedFilter] = useState<Filters>("ALL");
  const [showSettings, setShowSettings] = useState(false);
  const lastVoiceoverToastRef = useRef<number>(0);

  const { data, isLoading } = useCustomCaptionsQuery(teamExternalId);
  const allPresets = data?.captions ?? [];
  const customPresets = allPresets.filter((c) => c.teamExternalId !== null);
  const factoryPresets = [...allPresets].filter(
    (c) => c.teamExternalId === null,
  );
  const activeCustomPresets = customPresets.filter((c) => !c.deletedAt);
  const presets: CaptionDto[] = [...customPresets, ...factoryPresets];
  const activePresets: CaptionDto[] = [
    ...activeCustomPresets,
    ...factoryPresets,
  ];

  const referencedFontExternalIds = useMemo(() => {
    const ids = new Set<string>();
    for (const preset of presets) {
      ids.add(preset.config.style.text.fontExternalId);
    }
    return [...ids];
  }, [presets]);

  const { data: referencedFonts = [] } = useFontsByExternalIdsQuery(
    teamExternalId,
    referencedFontExternalIds,
  );

  const fontsMap = useMemo(
    () =>
      Object.fromEntries(
        referencedFonts.map((font) => [font.externalId, font]),
      ) as Record<string, FontDto>,
    [referencedFonts],
  );

  const captions = useEditorStore(
    (state) => state.video!.compositionJson.captions,
  );
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const addCompositionFont = useEditorStore(
    (state) => state.service.addCompositionFont,
  );
  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );

  const selectedPresetExternalId = captions?.externalId;
  const selectedPreset = presets.find(
    (preset) => preset.externalId === selectedPresetExternalId,
  );

  const filteredCaptions = useMemo(
    () =>
      getFilteredCaptions(selectedFilter, activePresets, activeCustomPresets),
    [selectedFilter, activePresets, activeCustomPresets],
  );

  const setCaptions = useCallback(
    (externalId: string | "NONE") => {
      if (externalId !== "NONE" && !currentVoiceOver) {
        const now = Date.now();
        if (now - lastVoiceoverToastRef.current > 10000) {
          lastVoiceoverToastRef.current = now;
          Toast.info(
            "Add a voiceover in the AI Voiceover tab in order to see captions.",
          );
        }
      }

      if (externalId !== "NONE") {
        const selectedPreset = presets.find(
          (preset) => preset.externalId === externalId,
        );

        const font =
          selectedPreset &&
          fontsMap[selectedPreset.config.style.text.fontExternalId];
        if (font) {
          addCompositionFont(font);
        }
      }

      updateVideo((video) => {
        if (externalId === "NONE") {
          video.compositionJson.captions = undefined;
          return;
        }

        const selectedPreset = presets.find(
          (preset) => preset.externalId === externalId,
        );

        if (selectedPreset) {
          video.compositionJson.captions = {
            externalId: selectedPreset.externalId,
            config: selectedPreset.config,
          };
        }
      });
    },
    [updateVideo, addCompositionFont, presets, fontsMap, currentVoiceOver],
  );

  const handleSelectPreset = (caption: CaptionDto) => {
    setCaptions(caption.externalId);
  };

  const handleClearSelection = () => {
    setCaptions("NONE");
  };

  const selectedItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSettings && selectedItemRef.current) {
      requestAnimationFrame(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: "instant",
          block: "nearest",
        });
      });
    }
  }, [showSettings, selectedFilter]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner size={24} />
      </div>
    );
  }

  if (showSettings && selectedPreset && captions) {
    return (
      <CaptionSettings
        key={selectedPreset.externalId}
        onBack={() => setShowSettings(false)}
        selectedPreset={selectedPreset}
        customCaptions={activeCustomPresets}
        compositionCaption={captions}
      />
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex h-full w-full flex-1 flex-col overflow-y-auto px-6 pt-5 pb-24">
        <div>
          <CaptionsFilters
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        </div>
        {activeCustomPresets.length === 0 &&
        selectedFilter === "CUSTOM_PRESETS" ? (
          <div className="flex h-full w-full flex-1 flex-col items-center justify-center">
            <div className="flex max-w-100 flex-col gap-2 text-center">
              <span className="font-medium text-text-primary">
                You haven’t created any custom presets yet.
              </span>
              <span className="max-w-prose text-center text-sm text-text-secondary">
                Start with a preset, customize it, and save it. Your custom
                presets will show up here.
              </span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {filteredCaptions.map((preset) => {
              const isSelected = selectedPresetExternalId === preset.externalId;

              return (
                <CaptionListItem
                  ref={isSelected ? selectedItemRef : undefined}
                  key={preset.externalId}
                  preset={preset}
                  fonts={fontsMap}
                  isSelected={isSelected}
                  onSelect={() => !isSelected && handleSelectPreset(preset)}
                />
              );
            })}
          </div>
        )}
      </div>
      <CaptionBottomBar
        selectedPreset={selectedPreset}
        onClear={handleClearSelection}
        onOpenSettings={() => setShowSettings(true)}
      />
    </div>
  );
}

const PREVIEW_FPS = 30;
const PREVIEW_DURATION_IN_FRAMES = Math.ceil(3.5 * PREVIEW_FPS);

function CaptionPreviewComposition({
  externalId,
  config,
  text,
  characterStartTimes,
  aspectRatio,
  fonts,
}: {
  externalId: string;
  config: CaptionConfigDto;
  text?: string;
  characterStartTimes?: number[];
  aspectRatio: CompositionAspectRatioDto;
  fonts: Record<string, FontDto>;
}) {
  return (
    <FontContext.Provider value={fonts}>
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
        <CaptionsComponent
          text={text ?? CAPTION_PREVIEW_TEXT}
          characterStartTimes={
            characterStartTimes ?? CAPTION_PREVIEW_CHARACTER_START_TIMES
          }
          compositionCaptions={{ externalId, config }}
          aspectRatio={aspectRatio}
          isPreview
        />
      </div>
    </FontContext.Provider>
  );
}

type CaptionListItemProps = {
  preset: CaptionDto;
  fonts: Record<string, FontDto>;
  isSelected: boolean;
  onSelect: () => void;
};

const CaptionListItem = React.forwardRef<HTMLDivElement, CaptionListItemProps>(
  ({ preset, fonts, isSelected, onSelect }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const playerRef = useRef<PlayerRef>(null);

    const handleMouseEnter = () => {
      setIsHovered(true);
      playerRef.current?.play();
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      playerRef.current?.pause();
      playerRef.current?.seekTo(0);
    };

    const previewWidth = 120;
    const previewHeight = 96;
    const previewScale = 1.4;

    const labelCharStartTimes = Array(preset.name.length).fill(0);

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex h-24 items-center justify-center overflow-hidden rounded-md border-2 border-transparent bg-text-primary",
        )}
        style={
          isSelected
            ? {
                background:
                  "linear-gradient(var(--color-black-400), var(--color-black-400)) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
              }
            : undefined
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isSelected && (
          <Checkbox
            checked={isSelected}
            className="absolute top-2 left-2 z-10"
            onClick={onSelect}
            size="sm"
          />
        )}
        <button
          className="absolute inset-0 z-5 rounded"
          aria-label={`Select ${preset.name} caption`}
          onClick={onSelect}
        />
        <div
          className="shrink-0"
          style={{
            width: previewWidth * previewScale,
            height: previewHeight * previewScale,
            pointerEvents: "none",
          }}
        >
          <Player
            ref={playerRef}
            component={CaptionPreviewComposition}
            numberOfSharedAudioTags={0}
            inputProps={
              isHovered
                ? {
                    externalId: preset.externalId,
                    config: preset.config,
                    fonts,
                    aspectRatio: "16:9",
                  }
                : {
                    externalId: preset.externalId,
                    config: {
                      ...preset.config,
                      displayMode:
                        preset.config.displayMode === "SINGLE_WORD"
                          ? "STATIC"
                          : preset.config.displayMode,
                      style: { ...preset.config.style, animation: undefined },
                    },
                    fonts,
                    text: preset.name,
                    characterStartTimes: labelCharStartTimes,
                    aspectRatio: "16:9",
                  }
            }
            durationInFrames={PREVIEW_DURATION_IN_FRAMES}
            compositionWidth={previewWidth * 2}
            compositionHeight={previewHeight * 2}
            fps={PREVIEW_FPS}
            loop
            style={{
              width: previewWidth * previewScale,
              height: previewHeight * previewScale,
            }}
            controls={false}
          />
        </div>
      </div>
    );
  },
);

CaptionListItem.displayName = "CaptionListItem";

type CaptionBottomBarProps = {
  selectedPreset: CaptionDto | null | undefined;
  onClear: () => void;
  onOpenSettings: () => void;
};

const CaptionBottomBar = ({
  selectedPreset,
  onClear,
  onOpenSettings,
}: CaptionBottomBarProps) => {
  if (!selectedPreset) {
    return null;
  }

  return (
    <div className="absolute bottom-0 z-10 flex min-h-18 w-full shrink-0 items-center justify-between border-t border-grey-200 bg-white px-6">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pr-4">
        <span className="text-xs text-text-secondary">Current Caption</span>
        <span className="truncate text-sm font-medium text-text-primary">
          {selectedPreset.name}
        </span>
      </div>
      <div className="flex flex-row gap-2.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="iconSm" onClick={onClear}>
              <IconX className="text-text-primary" size={18} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear current caption</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onOpenSettings}>
              Customize
            </Button>
          </TooltipTrigger>
          <TooltipContent>Customize caption styling</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
